import React, { useMemo, useState } from 'react';
import { Button, DialogContent } from '@mui/material';
import styles from './AddWorkoutsDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import SearchBar from '../atoms/SearchBar';
import MultiSelectList from '../composites/MultiSelectList';
import { calculateTemplateExercises } from '../../utils/dashboardUtils';
import { useSessionData } from '../../providers';

import type { TrainingRoutineTemplate } from '../../types';

export interface AddWorkoutsDialogProps {
  onSubmit: (workouts: TrainingRoutineTemplate[]) => void;
  onClose: () => void;
  singleSelect?: boolean; // Added singleSelect prop
}

const AddWorkoutsDialog: React.FC<AddWorkoutsDialogProps> = ({
  onClose,
  onSubmit,
  singleSelect = false, // Default to false if not provided
}) => {
  const { trainers, templates } = useSessionData();

  const workouts = useMemo(() => {
    const uniqueTemplates = new Map<string, TrainingRoutineTemplate>();

    Object.values(trainers).forEach(({ templates: trainerTemplates }) => {
      Object.values(trainerTemplates).forEach((template) => {
        uniqueTemplates.set(template.id, template);
      });
    });

    Object.values(templates).forEach((template) => {
      uniqueTemplates.set(template.id, template);
    });

    return Array.from(uniqueTemplates.values());
  }, [trainers, templates]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkouts, setSelectedWorkouts] = useState<
    TrainingRoutineTemplate[]
  >([]);

  const filteredWorkouts = workouts
    .filter(({ name }) =>
      name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  return (
    <BaseDialog
      onClose={onClose}
      isOpen={true}
      title="Choose or Search for a Workout"
      actions={
        <Button
          className={styles.submit}
          onClick={() => onSubmit(selectedWorkouts)}
        >
          <span className={styles.submitText}>Add</span>
        </Button>
      }
    >
      <DialogContent className={styles.addWorkoutsDialog}>
        <SearchBar
          className={styles.search}
          placeholder="Search"
          setSearchQuery={setSearchQuery}
        />
        <MultiSelectList
          items={filteredWorkouts}
          selectedItems={selectedWorkouts}
          setSelectedItems={setSelectedWorkouts}
          singleSelect={singleSelect} // Pass singleSelect to MultiSelectList
          EmptyStateComponent={() => null}
          ItemComponent={({ data }) => (
            <div className={styles.templateMultiSelectItem}>
              <div className={styles.templateName}>{data.name}</div>
              <div className={styles.templateExercises}>
                {calculateTemplateExercises(data.routine)} Exercises
              </div>
            </div>
          )}
        />
      </DialogContent>
    </BaseDialog>
  );
};

export default AddWorkoutsDialog;
